import * as Sentry from "@sentry/tanstackstart-react";
import { createMiddleware } from "@tanstack/react-start";

import {
  OtelServerTracingError,
  runWithServerOtelSpan,
} from "@/infrastructure/otel-server-tracing";
import { captureException } from "@/infrastructure/sentry-utils";
import { logger } from "@vivus/logger";

const SENTRY_SPAN_STATUS_OK = 1 as const;
const SENTRY_SPAN_STATUS_ERROR = 2 as const;

export function extractServerFunctionName(rawName: string) {
  if (!rawName.includes("--")) {
    return rawName;
  }

  const parts = rawName.split("--");
  const funcPart = parts[1]?.split("_createServerFn")[0];
  return funcPart ?? rawName;
}

export const observabilityFunctionMiddleware =
  createMiddleware({
    type: "function",
  })
    .client(async ({ next, serverFnMeta }) => {
      const startTime = performance.now();
      const functionName = extractServerFunctionName(
        serverFnMeta.id
      );

      try {
        return await next();
      } catch (error) {
        const duration = performance.now() - startTime;
        console.error(
          `[Client] ${functionName} failed after ${duration.toFixed(2)}ms:`,
          error
        );
        throw error;
      }
    })
    .server(async ({ next, data, serverFnMeta }) => {
      const functionName = extractServerFunctionName(
        serverFnMeta.name ||
          serverFnMeta.filename ||
          serverFnMeta.id
      );

      const startTime = Date.now();

      try {
        return await runWithServerOtelSpan(
          {
            name: `server.function ${functionName}`,
            kind: "internal",
            attributes: {
              "server.function.has_data":
                data !== undefined,
              "server.function.name": functionName,
            },
          },
          async () =>
            Sentry.startSpan(
              {
                op: "function.server",
                name: functionName,
                attributes: {
                  hasData: data !== undefined,
                  functionName,
                },
              },
              async (span) => {
                try {
                  const result = await next();

                  span.setStatus({
                    code: SENTRY_SPAN_STATUS_OK,
                  });

                  return result;
                } catch (error) {
                  const duration = Date.now() - startTime;

                  logger.error(
                    {
                      durationMs: duration,
                      error:
                        error instanceof Error
                          ? error.message
                          : error,
                      functionName,
                    },
                    "Server function failed"
                  );

                  captureException(error);
                  span.setStatus({
                    code: SENTRY_SPAN_STATUS_ERROR,
                    message: "internal_error",
                  });

                  throw error;
                }
              }
            )
        );
      } catch (error) {
        if (error instanceof OtelServerTracingError) {
          throw error.cause;
        }

        throw error;
      }
    });
